import { Injectable, Type } from '@angular/core';
import { ApexAxisChartSeries, ApexChart, ApexTitleSubtitle, ApexXAxis } from 'ng-apexcharts';
import { getMonthName } from '../../helper/month';
import { getTranslation } from '../../helper/translation';
import { ColumnDataType, ColumnDefinition, ColumnVisibility } from './../../model/column-definition';
import { AllowSort } from './allow-sort';
import { LanguageService } from '../language.service';

// Documentation of ApexChart options here: https://apexcharts.com/docs/options/xaxis/

export type ChartDefinition = {
  series: ApexAxisChartSeries | any;
  chart: ApexChart | any;
  xaxis: ApexXAxis | any;
  title: ApexTitleSubtitle | any;
};

@Injectable()
export class ChartDefinitionFactory {
  constructor(private readonly _languageService: LanguageService) {}
  create(
    header: string,
    series: { name: string; data: number[] }[],
    xLabels: string[],
    type:
      | 'line'
      | 'area'
      | 'bar'
      | 'radar'
      | 'histogram'
      | 'pie'
      | 'donut'
      | 'radialBar'
      | 'scatter'
      | 'bubble'
      | 'heatmap'
      | 'candlestick',
    height?: number | string
  ): ChartDefinition {
    let tran = this._languageService.getCurrentTranslations();
    let months = Array.from(Array(12).keys()).map(i => getMonthName(i + 1));
    let shortMonths = Array.from(Array(12).keys()).map(i => getMonthName(i + 1, true));
    let shortDays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(d => getTranslation(tran, 'CONST.WEEKDAYS.' + d));
    let days = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'].map(d =>
      getTranslation(tran, 'CONST.WEEKDAYS.' + d)
    );
    let def = {
      series: series.map((s, i) => {
        return { name: getTranslation(tran, s.name), data: s.data, color: this.getColor(i) };
      }),
      chart: {
        height: height ? height : 'auto',
        type: type,
        locales: [
          {
            name: 'en',
            options: {
              months: months,
              shortMonths: shortMonths,
              days: days,
              shortDays: shortDays,
              toolbar: {
                exportToSVG: getTranslation(tran, 'CHART.DOWNLOAD') + ' SVG',
                exportToPNG: getTranslation(tran, 'CHART.DOWNLOAD') + ' PNG',
                exportToCSV: getTranslation(tran, 'CHART.DOWNLOAD') + ' CSV',
                menu: getTranslation(tran, 'CHART.MENU'),
                selection: getTranslation(tran, 'CHART.SELECTION'),
                selectionZoom: getTranslation(tran, 'CHART.SELECTION_ZOOM'),
                zoomIn: getTranslation(tran, 'CHART.ZOOM_IN'),
                zoomOut: getTranslation(tran, 'CHART.ZOOM_OUT'),
                pan: getTranslation(tran, 'CHART.PAN'),
                reset: getTranslation(tran, 'COMMON.RESET'),
              },
            },
          },
        ],
        defaultLocale: 'en',
      },
      title: {
        text: getTranslation(tran, header),
        style: {
          fontFamily:
            '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"',
          fontSize: '1.1rem',
          fontWeight: 500,
        },
        offsetY: 20,
      },
      xaxis: {
        categories: xLabels.map(l => getTranslation(tran, l)),
      },
    };

    return def;
  }
  private getColor(seriesIndex: number) {
    if (seriesIndex === 0) {
      return '#27a69b';
    }
  }
}
